import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { StandardizedListing, STANDARD_LISTING_QUERY } from '@/utils/listingDataTransform';

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface PaginatedFilters {
  status?: string | string[];
  listingTypes?: string[];
  isBuyRequest?: boolean;
  featured?: boolean;
  userId?: string;
  manufacturers?: string[];
  models?: string[];
  condition?: string;
  priceMin?: number;
  priceMax?: number;
  yearMin?: number;
  yearMax?: number;
  seatingMin?: number;
  seatingMax?: number;
  searchQuery?: string;
  location?: string;
  boundingBox?: BoundingBox;
  sortBy?: string;
  // Job-specific filters
  jobCategories?: string[];
  employmentTypes?: string[];
  // Service-specific filters
  aogSupport?: boolean;
  serviceCategories?: string[];
  // Engine-specific filters
  tsnMin?: number;
  tsnMax?: number;
}

export interface PaginatedResult<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

const PAGE_SIZE = 20;

/** Escape LIKE/ILIKE wildcards in user input */
const escapeLike = (s: string) => s.replace(/%/g, '\\%').replace(/_/g, '\\_');

// ============= Targeted SELECT strings for card rendering =============
// Only fetch columns the card normalizer actually reads. Full data is fetched on detail modal open.

const ENGINE_CARD_SELECT = `
  id, title, manufacturer, model, variant, serial_number,
  listing_type, status, user_id, featured,
  price_usd, max_price, rental_rate, max_rate, rental_period, minimum_term, preferred_term, deposit,
  total_tsn, total_csn, last_overhaul_tso, last_overhaul_cso, recommended_tbo,
  power_output, power_unit, overall_condition,
  city, country, latitude, longitude,
  certifications, logbooks_available, accident_free, flexible_price,
  shipping_options, maintenance_responsibility,
  visibility_settings, payment_status, created_at, updated_at
`.replace(/\s+/g, ' ').trim();

const PARTS_CARD_SELECT = `
  id, title, name, listing_type, status, user_id, featured,
  price, max_budget, max_rate, rental_rate, rental_period, minimum_term, deposit,
  item_type, specificity_type, part_number, serial_number,
  helicopter_manufacturer, helicopter_model, helicopter_variant,
  engine_manufacturer, engine_model, engine_variant, engine_module,
  quantity, handling_time, condition,
  tsn, csn, tso, cso, life_limit, tbo,
  calibration_status, calibration_due_date, tool_type,
  equipment_category, power_requirements, capacity, operating_weight, material,
  certifications, warranty,
  city, country, latitude, longitude,
  hero_image,
  visibility_settings, payment_status, created_at, updated_at
`.replace(/\s+/g, ' ').trim();

const JOB_CARD_SELECT = `
  id, title, listing_type, status, user_id, featured,
  job_category, employment_type, remote_options,
  salary_min, salary_max, salary_type,
  company_name, company_logo, company_description, company_website,
  current_position, experience_years_min, experience_years_max,
  flight_hours_min, licenses, certifications, type_ratings, medical_class,
  benefits, requirements, responsibilities,
  relocation_offered, includes_benefits,
  hero_image,
  city, country, latitude, longitude,
  visibility_settings, payment_status, created_at, updated_at,
  search_vector
`.replace(/\s+/g, ' ').trim();

const SERVICE_CARD_SELECT = `
  id, title, listing_type, status, user_id, featured,
  service_category, company_name, company_logo,
  base_rate, rate_unit, pricing_model,
  regulatory_approvals, certifications, service_types,
  helicopter_capabilities, engine_capabilities,
  mission_types, response_time, turnaround_time,
  coverage_regions, service_radius,
  aog_support, aog_response_time, shift_coverage,
  facility_size_sqm, hangar_capacity, fleet_size,
  instructor_count, simulator_types, fuel_types, amenities,
  drone_types, flight_authorizations,
  hero_image, photos,
  city, country, latitude, longitude,
  visibility_settings, payment_status, created_at, updated_at,
  search_vector
`.replace(/\s+/g, ' ').trim();

// ============= Sort helpers =============

function applyHelicopterSort(query: any, sortBy?: string) {
  switch (sortBy) {
    case 'price-asc': return query.order('price_usd', { ascending: true, nullsFirst: false });
    case 'price-desc': return query.order('price_usd', { ascending: false, nullsFirst: false });
    case 'year-desc': return query.order('year_manufactured', { ascending: false, nullsFirst: false });
    case 'year-asc': return query.order('year_manufactured', { ascending: true, nullsFirst: false });
    case 'hours-asc': return query.order('total_flight_hours', { ascending: true, nullsFirst: false });
    case 'hours-desc': return query.order('total_flight_hours', { ascending: false, nullsFirst: false });
    case 'featured-first': return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
    case 'date-desc':
    default: return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
  }
}

function applyEngineSort(query: any, sortBy?: string) {
  switch (sortBy) {
    case 'price-asc': return query.order('price_usd', { ascending: true, nullsFirst: false });
    case 'price-desc': return query.order('price_usd', { ascending: false, nullsFirst: false });
    case 'tsn-asc': return query.order('total_tsn', { ascending: true, nullsFirst: false });
    case 'tsn-desc': return query.order('total_tsn', { ascending: false, nullsFirst: false });
    case 'featured-first': return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
    case 'date-desc':
    default: return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
  }
}

function applyPartsSort(query: any, sortBy?: string) {
  switch (sortBy) {
    case 'price-asc': return query.order('price', { ascending: true, nullsFirst: false });
    case 'price-desc': return query.order('price', { ascending: false, nullsFirst: false });
    case 'name-asc': return query.order('title', { ascending: true });
    case 'name-desc': return query.order('title', { ascending: false });
    case 'featured-first': return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
    case 'date-desc':
    default: return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
  }
}

function applyJobSort(query: any, sortBy?: string) {
  switch (sortBy) {
    case 'salary-desc': return query.order('salary_max', { ascending: false, nullsFirst: false });
    case 'salary-asc': return query.order('salary_min', { ascending: true, nullsFirst: false });
    case 'company-asc': return query.order('company_name', { ascending: true, nullsFirst: false });
    case 'company-desc': return query.order('company_name', { ascending: false, nullsFirst: false });
    case 'featured-first': return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
    case 'date-desc':
    default: return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
  }
}

function applyServiceSort(query: any, sortBy?: string) {
  switch (sortBy) {
    case 'price-asc': return query.order('base_rate', { ascending: true, nullsFirst: false });
    case 'price-desc': return query.order('base_rate', { ascending: false, nullsFirst: false });
    case 'company-asc': return query.order('company_name', { ascending: true, nullsFirst: false });
    case 'company-desc': return query.order('company_name', { ascending: false, nullsFirst: false });
    case 'featured-first': return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
    case 'date-desc':
    default: return query.order('featured', { ascending: false }).order('created_at', { ascending: false });
  }
}

/** Helper to extract hero image and photos from listing_media array */
function extractHeroAndPhotos(listing: any, media: any[]) {
  const urls = [...new Set(media.map(m => m.file_url).filter(Boolean))];
  const heroItems = media.filter(m => (m.metadata as any)?.category === 'hero');
  const bestHero = heroItems.length > 0
    ? heroItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
    : null;
  listing.heroImage = bestHero?.file_url || listing.hero_image || urls[0] || null;
  listing.hero_image = listing.heroImage;
  listing.photos = listing.heroImage ? [listing.heroImage, ...urls.filter(u => u !== listing.heroImage)] : urls;
}

/**
 * Fetch paginated helicopter listings with server-side filtering
 */
async function fetchPaginatedHelicopterListings(
  filters: PaginatedFilters,
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<PaginatedResult<StandardizedListing>> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // Parallelize cross-table lookups (search + seating) before main query
  const crossTablePromises: Promise<void>[] = [];

  if (filters.searchQuery) {
    const search = `%${escapeLike(filters.searchQuery)}%`;
    crossTablePromises.push(
      Promise.resolve(
        supabase
          .from('helicopters')
          .select('id')
          .or(`manufacturer.ilike.${search},model.ilike.${search}`)
          .limit(50)
      ).then(({ data }) => { (filters as any)._helicopterIds = data?.map(h => h.id) || []; })
        .catch(() => { (filters as any)._helicopterIds = []; })
    );
  }

  if (filters.seatingMin !== undefined || filters.seatingMax !== undefined) {
    let seatingQuery = supabase.from('helicopters').select('id');
    if (filters.seatingMin !== undefined) {
      seatingQuery = seatingQuery.gte('seating', filters.seatingMin);
    }
    if (filters.seatingMax !== undefined) {
      seatingQuery = seatingQuery.lte('seating', filters.seatingMax);
    }
    crossTablePromises.push(
      Promise.resolve(seatingQuery.limit(200)).then(({ data }) => {
        (filters as any)._seatingHelicopterIds = data?.map(h => h.id) || [];
      })
    );
  }

  // Wait for all cross-table lookups in parallel
  if (crossTablePromises.length > 0) {
    await Promise.all(crossTablePromises);
  }

  // Build count query (lightweight)
  let countQuery = supabase
    .from('helicopter_listings')
    .select('id', { count: 'exact', head: true });

  // Build data query
  let dataQuery = supabase
    .from('helicopter_listings')
    .select(STANDARD_LISTING_QUERY);

  // Apply filters to both queries
  const applyFilters = (query: any) => {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.listingTypes && filters.listingTypes.length > 0) {
      query = query.in('listing_type', filters.listingTypes);
    }

    if (filters.isBuyRequest !== undefined) {
      query = query.eq('is_buy_request', filters.isBuyRequest);
    }

    if (filters.featured !== undefined) {
      query = query.eq('featured', filters.featured);
    }

    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }

    // Server-side price filtering
    if (filters.priceMin !== undefined) {
      query = query.gte('price_usd', filters.priceMin);
    }
    if (filters.priceMax !== undefined) {
      query = query.lte('price_usd', filters.priceMax);
    }

    // Server-side year filtering
    if (filters.yearMin !== undefined) {
      query = query.gte('year_manufactured', filters.yearMin);
    }
    if (filters.yearMax !== undefined) {
      query = query.lte('year_manufactured', filters.yearMax);
    }

    // Payment status filter - only show paid listings
    if (!filters.userId) {
      query = query.eq('payment_status', 'paid');
    }

    // Server-side search (helicopter cross-table IDs injected externally)
    if (filters.searchQuery) {
      const search = `%${escapeLike(filters.searchQuery)}%`;
      if ((filters as any)._helicopterIds?.length > 0) {
        query = query.or(`title.ilike.${search},location.ilike.${search},helicopter_id.in.(${(filters as any)._helicopterIds.join(',')})`);
      } else {
        query = query.or(`title.ilike.${search},location.ilike.${search}`);
      }
    }

    // Location filter
    if (filters.location) {
      query = query.ilike('location', `%${filters.location}%`);
    }

    // Condition filter
    if (filters.condition && filters.condition !== 'all') {
      query = query.eq('condition', filters.condition);
    }

    // Seating range filter (cross-table lookup on helicopters)
    if (filters.seatingMin !== undefined || filters.seatingMax !== undefined) {
      if ((filters as any)._seatingHelicopterIds) {
        const ids = (filters as any)._seatingHelicopterIds;
        if (ids.length > 0) {
          query = query.in('helicopter_id', ids);
        } else {
          // No helicopters match seating range — return empty
          query = query.eq('id', '00000000-0000-0000-0000-000000000000');
        }
      }
    }

    // Geographic bounding box filter for map viewport optimization
    if (filters.boundingBox) {
      const { north, south, east, west } = filters.boundingBox;
      query = query
        .gte('latitude', south)
        .lte('latitude', north)
        .gte('longitude', west)
        .lte('longitude', east);
    }

    return query;
  };

  countQuery = applyFilters(countQuery);
  dataQuery = applyFilters(dataQuery);

  // Apply pagination and ordering to data query
  dataQuery = applyHelicopterSort(dataQuery, filters.sortBy);
  dataQuery = dataQuery.range(from, to);

  // Execute both queries in parallel
  const [countResult, dataResult] = await Promise.all([
    countQuery,
    dataQuery
  ]);

  if (dataResult.error) throw dataResult.error;

  const totalCount = countResult.count || 0;
  const listings = dataResult.data || [];

  // Fetch media and options in parallel for all listings
  if (listings.length > 0) {
    const listingIds = listings.map(l => l.id);

    // Collect option IDs while we wait for parallel fetches
    const allSelectedOptionIds: string[] = [];
    listings.forEach((listing: any) => {
      const opReqs = listing.operational_requirements;
      if (opReqs?.selectedOptions) {
        const options = Array.isArray(opReqs.selectedOptions) ? opReqs.selectedOptions : [];
        options.forEach((opt: any) => {
          const optId = typeof opt === 'string' ? opt : opt?.id;
          if (optId && typeof optId === 'string') {
            allSelectedOptionIds.push(optId);
          }
        });
      }
    });
    const uniqueOptionIds = [...new Set(allSelectedOptionIds)].slice(0, 100);

    // Parallel fetch: media + options
    const [mediaResult, optionsResult] = await Promise.all([
      supabase
        .from('listing_media')
        .select('listing_id, id, file_url, media_type, display_order, metadata')
        .in('listing_id', listingIds)
        .eq('media_type', 'photo')
        .order('display_order', { ascending: true }),
      uniqueOptionIds.length > 0
        ? supabase.from('v_options_with_icons').select('*').in('id', uniqueOptionIds)
        : Promise.resolve({ data: null }),
    ]);

    // Attach media to listings
    listings.forEach((listing: any) => {
      listing.listing_media = mediaResult.data?.filter(m => m.listing_id === listing.id) || [];
    });

    // Attach resolved options to listings
    if (optionsResult.data) {
      listings.forEach((listing: any) => {
        const opReqs = listing.operational_requirements;
        const optionIds = opReqs?.selectedOptions || [];
        listing.selected_options = optionIds
          .map((opt: any) => {
            const optId = typeof opt === 'string' ? opt : opt?.id;
            return optionsResult.data?.find((o: any) => o.id === optId);
          })
          .filter(Boolean);
      });
    }
  }

  return {
    data: listings as StandardizedListing[],
    totalCount,
    page,
    pageSize,
    hasMore: to < totalCount - 1
  };
}

/**
 * Hook for infinite scrolling helicopter listings with server-side pagination
 */
export function usePaginatedHelicopterListings(filters: PaginatedFilters) {
  return useInfiniteQuery({
    queryKey: ['helicopter-listings-paginated', filters],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchPaginatedHelicopterListings(filters, pageParam);
    },
    getNextPageParam: (lastPage) => 
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    enabled: filters.status !== 'disabled',
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch paginated engine listings
 */
async function fetchPaginatedEngineListings(
  filters: PaginatedFilters,
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<PaginatedResult<any>> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let countQuery = supabase
    .from('engine_listings')
    .select('id', { count: 'exact', head: true });

  let dataQuery = supabase
    .from('engine_listings')
    .select(ENGINE_CARD_SELECT as '*');

  // Apply filters
  const applyFilters = (query: any) => {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.listingTypes && filters.listingTypes.length > 0) {
      query = query.in('listing_type', filters.listingTypes);
    }
    if (filters.manufacturers && filters.manufacturers.length > 0) {
      query = query.in('manufacturer', filters.manufacturers);
    }
    if (filters.priceMin !== undefined) {
      query = query.gte('price_usd', filters.priceMin);
    }
    if (filters.priceMax !== undefined) {
      query = query.lte('price_usd', filters.priceMax);
    }
    if (!filters.userId) {
      query = query.eq('payment_status', 'paid');
    }

    if (filters.searchQuery) {
      const search = `%${escapeLike(filters.searchQuery)}%`;
      const tsQuery = filters.searchQuery.trim().split(/\s+/).join(' & ');
      query = query.or(`search_vector.fts.${tsQuery},title.ilike.${search},manufacturer.ilike.${search},model.ilike.${search}`);
    }
    if (filters.location) {
      query = query.or(`city.ilike.%${escapeLike(filters.location)}%,country.ilike.%${escapeLike(filters.location)}%`);
    }
    if (filters.condition && filters.condition !== 'all') {
      query = query.eq('overall_condition', filters.condition);
    }
    if (filters.tsnMin !== undefined) {
      query = query.gte('total_tsn', filters.tsnMin);
    }
    if (filters.tsnMax !== undefined) {
      query = query.lte('total_tsn', filters.tsnMax);
    }
    if (filters.boundingBox) {
      const { north, south, east, west } = filters.boundingBox;
      query = query
        .gte('latitude', south)
        .lte('latitude', north)
        .gte('longitude', west)
        .lte('longitude', east);
    }
    return query;
  };

  countQuery = applyFilters(countQuery);
  dataQuery = applyFilters(dataQuery);

  dataQuery = applyEngineSort(dataQuery, filters.sortBy);
  dataQuery = dataQuery.range(from, to);

  const [countResult, dataResult] = await Promise.all([countQuery, dataQuery]);

  if (dataResult.error) throw dataResult.error;

  const totalCount = countResult.count || 0;
  const listings = dataResult.data || [];

  // Batch fetch related data
  if (listings.length > 0) {
    const listingIds = listings.map(l => l.id);
    const userIds = [...new Set(listings.map(l => l.user_id).filter(Boolean))];

    const [modulesResult, mediaResult, profilesResult] = await Promise.all([
      supabase.from('engine_modules').select('*').in('engine_listing_id', listingIds),
      supabase.from('listing_media').select('listing_id, file_url, display_order, metadata, created_at')
        .in('listing_id', listingIds).eq('media_type', 'photo').order('display_order', { ascending: true }),
      supabase.from('profiles').select('user_id, display_name, email').in('user_id', userIds)
    ]);

    const allModules = modulesResult.data || [];
    const allMedia = mediaResult.data || [];
    const allProfiles = profilesResult.data || [];

    listings.forEach((listing: any) => {
      listing.engine_modules = allModules.filter(m => m.engine_listing_id === listing.id);
      const media = allMedia.filter(m => m.listing_id === listing.id);
      extractHeroAndPhotos(listing, media);
      const profile = allProfiles.find(p => p.user_id === listing.user_id);
      listing.seller_name = profile?.display_name || profile?.email;
    });
  }

  return {
    data: listings,
    totalCount,
    page,
    pageSize,
    hasMore: to < totalCount - 1
  };
}

export function usePaginatedEngineListings(filters: PaginatedFilters) {
  return useInfiniteQuery({
    queryKey: ['engine-listings-paginated', filters],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchPaginatedEngineListings(filters, pageParam);
    },
    getNextPageParam: (lastPage) => 
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    enabled: filters.status !== 'disabled',
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch paginated parts listings
 */
async function fetchPaginatedPartsListings(
  filters: PaginatedFilters,
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<PaginatedResult<any>> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let countQuery = supabase
    .from('parts_listings')
    .select('id', { count: 'exact', head: true });

  let dataQuery = supabase
    .from('parts_listings')
    .select(PARTS_CARD_SELECT as '*');

  const applyFilters = (query: any) => {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.listingTypes && filters.listingTypes.length > 0) {
      query = query.in('listing_type', filters.listingTypes);
    }
    if (filters.priceMin !== undefined) {
      query = query.gte('price', filters.priceMin);
    }
    if (filters.priceMax !== undefined) {
      query = query.lte('price', filters.priceMax);
    }
    if (!filters.userId) {
      query = query.eq('payment_status', 'paid');
    }

    if (filters.searchQuery) {
      const search = `%${escapeLike(filters.searchQuery)}%`;
      const tsQuery = filters.searchQuery.trim().split(/\s+/).join(' & ');
      query = query.or(`search_vector.fts.${tsQuery},part_number.ilike.${search},title.ilike.${search}`);
    }
    if (filters.location) {
      query = query.or(`city.ilike.%${escapeLike(filters.location)}%,country.ilike.%${escapeLike(filters.location)}%`);
    }
    if (filters.condition && filters.condition !== 'all') {
      query = query.eq('condition', filters.condition);
    }
    if (filters.boundingBox) {
      const { north, south, east, west } = filters.boundingBox;
      query = query
        .gte('latitude', south)
        .lte('latitude', north)
        .gte('longitude', west)
        .lte('longitude', east);
    }
    return query;
  };

  countQuery = applyFilters(countQuery);
  dataQuery = applyFilters(dataQuery);

  dataQuery = applyPartsSort(dataQuery, filters.sortBy);
  dataQuery = dataQuery.range(from, to);

  const [countResult, dataResult] = await Promise.all([countQuery, dataQuery]);

  if (dataResult.error) throw dataResult.error;

  const totalCount = countResult.count || 0;
  const listings = dataResult.data || [];

  // Batch fetch listing_media for gallery images
  if (listings.length > 0) {
    const listingIds = listings.map(l => l.id);
    const { data: mediaData } = await supabase
      .from('listing_media')
      .select('listing_id, file_url, display_order, metadata, created_at')
      .in('listing_id', listingIds)
      .eq('media_type', 'photo')
      .order('display_order', { ascending: true });

    listings.forEach((listing: any) => {
      const media = mediaData?.filter(m => m.listing_id === listing.id) || [];
      listing.listing_media = media;
      extractHeroAndPhotos(listing, media);
    });
  }

  return {
    data: listings,
    totalCount,
    page,
    pageSize,
    hasMore: to < totalCount - 1
  };
}

export function usePaginatedPartsListings(filters: PaginatedFilters) {
  return useInfiniteQuery({
    queryKey: ['parts-listings-paginated', filters],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchPaginatedPartsListings(filters, pageParam);
    },
    getNextPageParam: (lastPage) => 
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    enabled: filters.status !== 'disabled',
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch paginated job listings
 */
async function fetchPaginatedJobListings(
  filters: PaginatedFilters,
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<PaginatedResult<any>> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let countQuery = supabase
    .from('job_listings')
    .select('id', { count: 'exact', head: true });

  let dataQuery = supabase
    .from('job_listings')
    .select(JOB_CARD_SELECT as '*');

  const applyFilters = (query: any) => {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.listingTypes && filters.listingTypes.length > 0) {
      query = query.in('listing_type', filters.listingTypes);
    }
    if (!filters.userId) {
      query = query.eq('payment_status', 'paid');
    }

    if (filters.searchQuery) {
      const search = `%${escapeLike(filters.searchQuery)}%`;
      const tsQuery = filters.searchQuery.trim().split(/\s+/).join(' & ');
      query = query.or(`search_vector.fts.${tsQuery},title.ilike.${search},company_name.ilike.${search}`);
    }
    if (filters.location) {
      query = query.or(`city.ilike.%${escapeLike(filters.location)}%,country.ilike.%${escapeLike(filters.location)}%`);
    }
    if (filters.jobCategories && filters.jobCategories.length > 0) {
      query = query.in('job_category', filters.jobCategories);
    }
    if (filters.employmentTypes && filters.employmentTypes.length > 0) {
      query = query.in('employment_type', filters.employmentTypes);
    }
    if (filters.boundingBox) {
      const { north, south, east, west } = filters.boundingBox;
      query = query
        .gte('latitude', south)
        .lte('latitude', north)
        .gte('longitude', west)
        .lte('longitude', east);
    }
    return query;
  };

  countQuery = applyFilters(countQuery);
  dataQuery = applyFilters(dataQuery);

  dataQuery = applyJobSort(dataQuery, filters.sortBy);
  dataQuery = dataQuery.range(from, to);

  const [countResult, dataResult] = await Promise.all([countQuery, dataQuery]);

  if (dataResult.error) throw dataResult.error;

  const totalCount = countResult.count || 0;
  const listings = dataResult.data || [];

  // Batch fetch listing_media for gallery images
  if (listings.length > 0) {
    const listingIds = listings.map(l => l.id);
    const { data: mediaData } = await supabase
      .from('listing_media')
      .select('listing_id, file_url, display_order, metadata, created_at')
      .in('listing_id', listingIds)
      .eq('media_type', 'photo')
      .order('display_order', { ascending: true });

    listings.forEach((listing: any) => {
      const media = mediaData?.filter(m => m.listing_id === listing.id) || [];
      listing.listing_media = media;
      extractHeroAndPhotos(listing, media);
    });
  }

  return {
    data: listings,
    totalCount,
    page,
    pageSize,
    hasMore: to < totalCount - 1
  };
}

export function usePaginatedJobListings(filters: PaginatedFilters) {
  return useInfiniteQuery({
    queryKey: ['job-listings-paginated', filters],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchPaginatedJobListings(filters, pageParam);
    },
    getNextPageParam: (lastPage) => 
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    enabled: filters.status !== 'disabled',
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch paginated service listings
 */
async function fetchPaginatedServiceListings(
  filters: PaginatedFilters & { serviceCategory?: string; serviceCategories?: string[]; aogSupport?: boolean },
  page: number,
  pageSize: number = PAGE_SIZE
): Promise<PaginatedResult<any>> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let countQuery = supabase
    .from('service_listings')
    .select('id', { count: 'exact', head: true });

  let dataQuery = supabase
    .from('service_listings')
    .select(SERVICE_CARD_SELECT as '*');

  const applyFilters = (query: any) => {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.serviceCategories && filters.serviceCategories.length > 0) {
      query = query.in('service_category', filters.serviceCategories);
    } else if (filters.serviceCategory) {
      query = query.eq('service_category', filters.serviceCategory);
    }
    if (filters.listingTypes && filters.listingTypes.length > 0) {
      query = query.in('listing_type', filters.listingTypes);
    }
    if (filters.aogSupport) {
      query = query.eq('aog_support', true);
    }
    if (!filters.userId) {
      query = query.eq('payment_status', 'paid');
    }

    if (filters.searchQuery) {
      const search = `%${escapeLike(filters.searchQuery)}%`;
      const tsQuery = filters.searchQuery.trim().split(/\s+/).join(' & ');
      query = query.or(`search_vector.fts.${tsQuery},title.ilike.${search},company_name.ilike.${search}`);
    }
    if (filters.location) {
      query = query.or(`city.ilike.%${escapeLike(filters.location)}%,country.ilike.%${escapeLike(filters.location)}%`);
    }
    if (filters.boundingBox) {
      const { north, south, east, west } = filters.boundingBox;
      query = query
        .gte('latitude', south)
        .lte('latitude', north)
        .gte('longitude', west)
        .lte('longitude', east);
    }
    return query;
  };

  countQuery = applyFilters(countQuery);
  dataQuery = applyFilters(dataQuery);

  dataQuery = applyServiceSort(dataQuery, filters.sortBy);
  dataQuery = dataQuery.range(from, to);

  const [countResult, dataResult] = await Promise.all([countQuery, dataQuery]);

  if (dataResult.error) throw dataResult.error;

  const totalCount = countResult.count || 0;
  const listings = dataResult.data || [];

  // Batch fetch listing_media for gallery images
  if (listings.length > 0) {
    const listingIds = listings.map(l => l.id);
    const { data: mediaData } = await supabase
      .from('listing_media')
      .select('listing_id, file_url, display_order, metadata, created_at')
      .in('listing_id', listingIds)
      .eq('media_type', 'photo')
      .order('display_order', { ascending: true });

    listings.forEach((listing: any) => {
      const media = mediaData?.filter(m => m.listing_id === listing.id) || [];
      listing.listing_media = media;
      extractHeroAndPhotos(listing, media);
    });
  }

  return {
    data: listings,
    totalCount,
    page,
    pageSize,
    hasMore: to < totalCount - 1
  };
}

export function usePaginatedServiceListings(filters: PaginatedFilters & { serviceCategory?: string; serviceCategories?: string[]; aogSupport?: boolean }) {
  return useInfiniteQuery({
    queryKey: ['service-listings-paginated', filters],
    queryFn: async ({ pageParam = 1 }) => {
      return fetchPaginatedServiceListings(filters, pageParam);
    },
    getNextPageParam: (lastPage) => 
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    enabled: filters.status !== 'disabled',
    placeholderData: (previousData) => previousData,
  });
}
